import {
  ChecklistDTO,
  SupportingItemInputDTO,
  SupportingItemInputTypeDiscriminatorDTO,
  SectionDTO,
  RequestedSupportingItemDTO,
  DocumentSupportingItemInputTypeDTO,
  PersonalInfoSupportingItemInputTypeDTO,
  DependentsInfoSupportingItemInputTypeDTO,
  SpousalInfoSupportingItemInputTypeDTO,
  PersonalRefundMethodInfoSupportingItemInputTypeDTO,
  NotesSupportingItemInputTypeDTO,
  BusinessRefundMethodInfoSupportingItemInputTypeDTO,
  BusinessInfoSupportingItemInputTypeDTO,
} from './dtos/info-gathering'
import { Model } from '../infra/model'
import { observable, makeObservable, computed } from 'mobx'
import { collection } from '../infra/collection'
import { maybeParseDate, DateZero } from '../utils/date-util'
import { PrimeDocument } from '../documents/DocumentStore'
import { isNotNil } from '../utils/object-util'
import Document from '../documents/Document'
import DocumentType from '../documents/DocumentType'
import { PrimeDocumentTypeByShortId } from '../documents/DocumentTypeStore'
import orderBy from 'lodash/orderBy'

export default class Checklist extends Model<ChecklistDTO, Checklist> {
  jobId: string = ''
  sections = collection<SectionDTO, Section>({
    create: () => new Section(this.primeDocument, this.primeDocumentType),
  })

  constructor(
    private readonly primeDocument: PrimeDocument,
    private readonly primeDocumentType: PrimeDocumentTypeByShortId,
  ) {
    super()
    makeObservable(this, {
      jobId: observable,
      sections: observable,
      notesSection: computed,
      businessInfoSection: computed,
    })
  }

  get notesSection() {
    return this.sections.items.find(
      (section) => section.id === WellKnownSectionCategoryId.Notes,
    )
  }

  get personalInfoSection() {
    return this.sections.items.find(
      (section) =>
        section.id === WellKnownSectionCategoryId.PersonalInformation,
    )
  }

  get businessInfoSection() {
    return this.sections.items.find(
      (section) =>
        section.id === WellKnownSectionCategoryId.BusinessInformation,
    )
  }

  /**
   * Gets the requested item by ID.
   *
   * @param id ID of the item to get.
   */
  getItemById(id: string) {
    for (const section of this.sections.items) {
      for (const item of section.requestedSupportingItems.items) {
        if (item.id === id) {
          return item
        }
      }
    }
    return null
  }

  parse(json: ChecklistDTO): Partial<Checklist> {
    return {
      jobId: json.job_id,
      sections: this.sections.reset(json.sections),
    }
  }
}

export class Section extends Model<SectionDTO, Section> {
  id: string = ''
  name: string = ''
  heading: string = ''
  description: string = ''
  requestedSupportingItems = collection<
    RequestedSupportingItemDTO,
    RequestedSupportingItem
  >({
    create: () =>
      new RequestedSupportingItem(this.primeDocument, this.primeDocumentType),
  })

  constructor(
    private readonly primeDocument: PrimeDocument,
    private readonly primeDocumentType: PrimeDocumentTypeByShortId,
  ) {
    super()
    makeObservable(this, {
      id: observable,
      name: observable,
      heading: observable,
      description: observable,
      requestedSupportingItems: observable,
      notesSupportingItem: computed,
      businessInfoSupportingItem: computed,
      requirementsCompleted: computed,
      completedCount: computed,
      totalCount: computed,
    })
  }

  get notesSupportingItem() {
    return this.requestedSupportingItems.find(
      (requestedSupportingItem) =>
        requestedSupportingItem.input instanceof NotesSupportingItemInput,
    )
  }

  get personalInfoSupportingItem() {
    return this.requestedSupportingItems.find(
      (requestedSupportingItem) =>
        requestedSupportingItem.input instanceof
        PersonalInfoSupportingItemInput,
    )
  }

  get dependentInfoSupportingItem() {
    return this.requestedSupportingItems.find(
      (requestedSupportingItem) =>
        requestedSupportingItem.input instanceof
        DependentsInfoSupportingItemInput,
    )
  }

  get spousalInfoSupportingItem() {
    return this.requestedSupportingItems.find(
      (requestedSupportingItem) =>
        requestedSupportingItem.input instanceof SpousalInfoSupportingItemInput,
    )
  }

  get businessInfoSupportingItem() {
    return this.requestedSupportingItems.find(
      (requestedSupportingItem) =>
        requestedSupportingItem.input instanceof
        BusinessInfoSupportingItemInput,
    )
  }

  get businessRefundMethodInfoSupportingItem() {
    return this.requestedSupportingItems.find(
      (requestedSupportingItem) =>
        requestedSupportingItem.input instanceof
        BusinessRefundMethodInfoSupportingItemInput,
    )
  }

  get personalRefundMethodInfoSupportingItem() {
    return this.requestedSupportingItems.find(
      (requestedSupportingItem) =>
        requestedSupportingItem.input instanceof
        PersonalRefundMethodInfoSupportingItemInput,
    )
  }

  get requirementsCompleted(): boolean {
    return this.requestedSupportingItems.every(
      (requestedSupportingItem) =>
        (requestedSupportingItem.required &&
          requestedSupportingItem.completed) ||
        !requestedSupportingItem.required,
    )
  }

  get completedCount(): number {
    return this.requestedSupportingItems.filter(
      (requestedSupportingItem) => requestedSupportingItem.completed,
    ).length
  }

  get totalCount(): number {
    return this.requestedSupportingItems.length
  }

  parse(json: SectionDTO) {
    const itemDtos =
      json.requested_supporting_items &&
      orderBy(json.requested_supporting_items, (x) => x.required, 'desc')

    return {
      id: json.id,
      name: json.name,
      heading: json.heading,
      description: json.description,
      requestedSupportingItems: this.requestedSupportingItems.reset(itemDtos),
    }
  }
}

export class RequestedSupportingItem extends Model<
  RequestedSupportingItemDTO,
  RequestedSupportingItem
> {
  id: string = ''
  name: string = ''
  heading: string = ''
  shortDescription: string = ''
  longDescription: string = ''
  imageUrl?: string = ''
  previewUrl?: string = ''
  required: boolean = false
  adHoc: boolean = false
  completed: boolean = false
  dateModified: Date = DateZero()
  input?: SupportingItemInput = undefined

  constructor(
    private readonly primeDocument: PrimeDocument,
    private readonly primeDocumentType: PrimeDocumentTypeByShortId,
  ) {
    super()
    makeObservable(this, {
      id: observable,
      name: observable,
      heading: observable,
      shortDescription: observable,
      longDescription: observable,
      imageUrl: observable,
      previewUrl: observable,
      required: observable,
      adHoc: observable,
      completed: observable,
      input: observable,
    })
  }

  parse(json: RequestedSupportingItemDTO) {
    return {
      id: json.id,
      name: json.name,
      heading: json.heading,
      shortDescription: json.short_description,
      longDescription: json.long_description,
      imageUrl: json.image_url,
      previewUrl: json.preview_url,
      required: json.required,
      adHoc: json.adHoc,
      completed: json.completed,
      dateModified: maybeParseDate(json.update_time),
      input: parseSupportingItemInputType(
        json.input,
        this.primeDocument,
        this.primeDocumentType,
      ),
    }
  }
}

function parseSupportingItemInputType(
  input: SupportingItemInputDTO,
  primeDocument: PrimeDocument,
  primeDocumentType: PrimeDocumentTypeByShortId,
): SupportingItemInput | undefined {
  switch (input.type.discriminator) {
    case SupportingItemInputTypeDiscriminatorDTO.DOCUMENT:
      return new DocumentSupportingItemInput(
        primeDocument,
        primeDocumentType,
      ).set(input.type, { parse: true, stripUndefined: true })
    case SupportingItemInputTypeDiscriminatorDTO.PERSONAL_INFO:
      return new PersonalInfoSupportingItemInput().set(input.type, {
        parse: true,
        stripUndefined: false,
      })
    case SupportingItemInputTypeDiscriminatorDTO.SPOUSAL_INFO:
      return new SpousalInfoSupportingItemInput().set(input.type, {
        parse: true,
        stripUndefined: false,
      })
    case SupportingItemInputTypeDiscriminatorDTO.DEPENDENTS_INFO:
      return new DependentsInfoSupportingItemInput().set(input.type, {
        parse: true,
        stripUndefined: false,
      })
    case SupportingItemInputTypeDiscriminatorDTO.PERSONAL_REFUND_METHOD_INFO:
      return new PersonalRefundMethodInfoSupportingItemInput().set(input.type, {
        parse: true,
        stripUndefined: false,
      })
    case SupportingItemInputTypeDiscriminatorDTO.BUSINESS_INFO:
      return new BusinessInfoSupportingItemInput().set(input.type, {
        parse: true,
        stripUndefined: false,
      })
    case SupportingItemInputTypeDiscriminatorDTO.BUSINESS_REFUND_METHOD_INFO:
      return new BusinessRefundMethodInfoSupportingItemInput().set(input.type, {
        parse: true,
        stripUndefined: false,
      })
    case SupportingItemInputTypeDiscriminatorDTO.NOTES:
      return new NotesSupportingItemInput().set(input.type, {
        parse: true,
        stripUndefined: false,
      })

    default:
      throw new Error('Unsupported input type discriminator')
  }
}

export type SupportingItemInput =
  | DocumentSupportingItemInput
  | PersonalInfoSupportingItemInput
  | SpousalInfoSupportingItemInput
  | DependentsInfoSupportingItemInput
  | PersonalRefundMethodInfoSupportingItemInput
  | BusinessInfoSupportingItemInput
  | BusinessRefundMethodInfoSupportingItemInput
  | NotesSupportingItemInput

export class DocumentSupportingItemInput extends Model<
  DocumentSupportingItemInputTypeDTO,
  DocumentSupportingItemInput
> {
  documentType = DocumentType.none
  documents: Document[] = []

  constructor(
    private readonly primeDocument: PrimeDocument,
    private readonly primeDocumentType: PrimeDocumentTypeByShortId,
  ) {
    super()
    makeObservable(this, {
      documentType: observable,
      documents: observable,
    })
  }

  parse(json: Partial<DocumentSupportingItemInputTypeDTO>) {
    const documents = json.document?.document_long_ids
      .map((long_id) => this.primeDocument({ long_id }))
      .filter(isNotNil)
    return {
      documentType: json.document?.document_type_short_id
        ? this.primeDocumentType(json.document?.document_type_short_id)
        : undefined,
      documents,
    }
  }
}

export class PersonalInfoSupportingItemInput extends Model<
  PersonalInfoSupportingItemInputTypeDTO,
  PersonalInfoSupportingItemInput
> {
  profileId?: string = undefined
  profileVersion?: number = undefined

  constructor() {
    super()
    makeObservable(this, {
      profileId: observable,
      profileVersion: observable,
    })
  }

  parse(
    json: Partial<PersonalInfoSupportingItemInputTypeDTO>,
  ): Partial<PersonalInfoSupportingItemInput> {
    return {
      profileId: json.personal_info?.profile_id,
      profileVersion: json.personal_info?.profile_version,
    }
  }
}

export class SpousalInfoSupportingItemInput extends Model<
  SpousalInfoSupportingItemInputTypeDTO,
  SpousalInfoSupportingItemInput
> {
  profileId?: string = undefined
  profileVersion?: number = undefined

  constructor() {
    super()
    makeObservable(this, {
      profileId: observable,
      profileVersion: observable,
    })
  }

  parse(
    json: Partial<SpousalInfoSupportingItemInputTypeDTO>,
  ): Partial<SpousalInfoSupportingItemInput> {
    return {
      profileId: json.spousal_info?.profile_id,
      profileVersion: json.spousal_info?.profile_version,
    }
  }
}

export class DependentsInfoSupportingItemInput extends Model<
  DependentsInfoSupportingItemInputTypeDTO,
  DependentsInfoSupportingItemInput
> {
  dependents: Array<{
    profileId?: string
    profileVersion?: number
  }> = []

  constructor() {
    super()
    makeObservable(this, {
      dependents: observable,
    })
  }

  parse(
    json: Partial<DependentsInfoSupportingItemInputTypeDTO>,
  ): Partial<DependentsInfoSupportingItemInput> {
    return {
      dependents:
        json.dependents?.map((dependent) => ({
          profileId: dependent.profile_id,
          profileVersion: dependent.profile_version,
        })) || [],
    }
  }
}

export class PersonalRefundMethodInfoSupportingItemInput extends Model<
  PersonalRefundMethodInfoSupportingItemInputTypeDTO,
  PersonalRefundMethodInfoSupportingItemInput
> {
  refundMethodId: string = ''
  refundMethodVersion?: number = undefined

  constructor() {
    super()
    makeObservable(this, {
      refundMethodId: observable,
      refundMethodVersion: observable,
    })
  }

  parse(
    json: Partial<PersonalRefundMethodInfoSupportingItemInputTypeDTO>,
  ): Partial<PersonalRefundMethodInfoSupportingItemInput> {
    return {
      refundMethodId: json.personal_refund_method_info?.refund_method_id,
      refundMethodVersion:
        json.personal_refund_method_info?.refund_method_version,
    }
  }
}

export class BusinessInfoSupportingItemInput extends Model<
  BusinessInfoSupportingItemInputTypeDTO,
  BusinessInfoSupportingItemInput
> {
  profileId?: string = undefined
  profileVersion?: number = undefined

  constructor() {
    super()
    makeObservable(this, {
      profileId: observable,
      profileVersion: observable,
    })
  }

  parse(
    json: Partial<BusinessInfoSupportingItemInputTypeDTO>,
  ): Partial<BusinessInfoSupportingItemInput> {
    return {
      profileId: json.business_info?.profile_id,
      profileVersion: json.business_info?.profile_version,
    }
  }
}

export class BusinessRefundMethodInfoSupportingItemInput extends Model<
  BusinessRefundMethodInfoSupportingItemInputTypeDTO,
  BusinessRefundMethodInfoSupportingItemInput
> {
  refundMethodId: string = ''
  refundMethodVersion?: number = undefined

  constructor() {
    super()
    makeObservable(this, {
      refundMethodId: observable,
      refundMethodVersion: observable,
    })
  }

  parse(
    json: Partial<BusinessRefundMethodInfoSupportingItemInputTypeDTO>,
  ): Partial<BusinessRefundMethodInfoSupportingItemInput> {
    return {
      refundMethodId: json.business_refund_method_info?.refund_method_id,
      refundMethodVersion:
        json.business_refund_method_info?.refund_method_version,
    }
  }
}

export class NotesSupportingItemInput extends Model<
  NotesSupportingItemInputTypeDTO,
  NotesSupportingItemInput
> {
  text?: string = undefined

  constructor() {
    super()
    makeObservable(this, {
      text: observable,
    })
  }

  parse(
    json: Partial<NotesSupportingItemInputTypeDTO>,
  ): Partial<NotesSupportingItemInput> {
    return {
      text: json.notes?.text,
    }
  }
}

export enum WellKnownSectionCategoryId {
  PersonalInformation = 'PERSONAL_INFORMATION',
  BusinessInformation = 'BUSINESS_INFORMATION',
  Notes = 'NOTES',
}
